import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Home, Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

type Phase = 'rest' | 'inhale' | 'hold' | 'exhale';

export default function Breathe() {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('rest');
  const [count, setCount] = useState(0);
  const [music, setMusic] = useState(false);
  const [volume, setVolume] = useState(30);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useSeoMeta({ title: 'Breathe - Zenstr' });

  useEffect(() => {
    audioRef.current = new Audio('https://cdn.pixabay.com/audio/2022/05/13/audio_2b2b3ba446.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = volume / 100;
    return () => audioRef.current?.pause();
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
  }, [volume]);

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      setCount(prev => {
        if (prev > 1) return prev - 1;
        switch (phase) {
          case 'rest': setPhase('inhale'); return 4;
          case 'inhale': setPhase('hold'); return 7;
          case 'hold': setPhase('exhale'); return 8;
          case 'exhale': setPhase('rest'); return 2;
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [active, phase]);

  const start = () => {
    setActive(true);
    setPhase('rest');
    setCount(1);
    if (music && audioRef.current) audioRef.current.play();
  };

  const pause = () => {
    setActive(false);
    audioRef.current?.pause();
  };

  const reset = () => {
    setActive(false);
    setPhase('rest');
    setCount(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const phaseText = { rest: 'Ready', inhale: 'Breathe In', hold: 'Hold', exhale: 'Breathe Out' }[phase];
  const phaseColor = { rest: 'from-gray-500 to-gray-600', inhale: 'from-cyan-500 to-blue-500', hold: 'from-blue-500 to-purple-500', exhale: 'from-purple-500 to-pink-500' }[phase];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🧘‍♀️</span>
            <span className="font-display font-semibold">Zenstr</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" /> Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-16 max-w-4xl">
        <div className="text-center space-y-12">
          <div className="space-y-2">
            <h1 className="text-5xl md:text-6xl font-display font-bold">Breathe</h1>
            <p className="text-xl text-muted-foreground">4-7-8 Technique for Deep Relaxation</p>
          </div>

          {/* Orb */}
          <div className="relative flex items-center justify-center py-12">
            <div 
              className={`w-80 h-80 rounded-full bg-gradient-to-br ${phaseColor} shadow-2xl flex flex-col items-center justify-center transition-all duration-1000 ${active ? 'breathe-cycle' : 'scale-100'}`}
            >
              <h2 className="text-3xl font-display font-bold text-white mb-4">{phaseText}</h2>
              {active && count > 0 && <div className="text-8xl font-display font-bold text-white">{count}</div>}
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-3 justify-center">
            {!active ? (
              <Button onClick={start} size="lg" className="px-10 py-6 text-lg"><Play className="w-5 h-5 mr-2" /> Start</Button>
            ) : (
              <Button onClick={pause} size="lg" variant="outline" className="px-10 py-6 text-lg"><Pause className="w-5 h-5 mr-2" /> Pause</Button>
            )}
            <Button onClick={reset} size="lg" variant="outline"><RotateCcw className="w-5 h-5 mr-2" /> Reset</Button>
            <Button onClick={() => { setMusic(!music); !music && active && audioRef.current?.play(); music && audioRef.current?.pause(); }} size="lg" variant="outline">
              {music ? <Volume2 className="w-5 h-5 mr-2" /> : <VolumeX className="w-5 h-5 mr-2" />}
              Music
            </Button>
          </div>

          {music && (
            <div className="max-w-xs mx-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span>Volume</span>
                <span>{volume}%</span>
              </div>
              <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={100} />
            </div>
          )}

          <Card className="p-8 bg-card/50 max-w-2xl mx-auto">
            <h3 className="font-display font-semibold text-lg mb-3">The 4-7-8 Technique</h3>
            <p className="text-muted-foreground leading-relaxed">
              Developed by Dr. Andrew Weil, this breathing pattern activates your parasympathetic nervous system. 
              Inhale for 4 seconds, hold for 7 seconds, exhale for 8 seconds, rest for 2 seconds.
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
}
